import { useEffect, useRef, useState } from "react";
import { api, API, TOKEN_KEY } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sparkles, Send, Loader2, User, Bot, RotateCcw, AlertTriangle, CalendarClock, ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import { money } from "@/lib/format";

const SESSION_KEY = "finakis_assistant_session";

// Minimal markdown: **bold** + preserve line breaks (no external dependency)
function renderRich(text) {
  return text.split("\n").map((line, li) => (
    <span key={li}>
      {line.split(/(\*\*[^*]+\*\*)/g).map((part, pi) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={pi} className="font-semibold text-primary">{part.slice(2, -2)}</strong>
        ) : (
          <span key={pi}>{part}</span>
        )
      )}
      {li < text.split("\n").length - 1 && <br />}
    </span>
  ));
}

const SUGGESTIONS = [
  "En yüksek alacaklı müşterim kim?",
  "Toplam borcum ne kadar?",
  "Bu ayki net nakit akışım nedir?",
  "Net ödenecek KDV ne kadar?",
];

function getSessionId() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
}

export default function Assistant() {
  const [sessionId, setSessionId] = useState(getSessionId);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [summary, setSummary] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    api.assistantHistory(sessionId).then((d) => setMessages(d.messages || [])).catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    api.dailySummary(false).then(setSummary).catch(() => {});
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
        },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          const t = line.trim();
          if (!t.startsWith("data:")) continue;
          const payload = JSON.parse(t.slice(5).trim());
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + payload.delta };
              return copy;
            });
          } else if (payload.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", text: "Üzgünüm, bir hata oluştu: " + payload.error };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Bağlantı hatası. Lütfen tekrar deneyin." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const newChat = () => {
    const s = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(SESSION_KEY, s);
    setMessages([]);
    setSessionId(s);
  };

  return (
    <div className="p-6 lg:p-8" data-testid="assistant-page">
      <PageHeader
        title="Finans Asistanı"
        subtitle="Verilerinize dayalı sorular sorun · gpt-5.2"
        action={
          <Button data-testid="assistant-new-chat" variant="outline" size="sm" onClick={newChat} className="gap-1.5">
            <RotateCcw className="h-4 w-4" /> Yeni Sohbet
          </Button>
        }
      />

      {summary && (summary.critical_stock.count > 0 || summary.upcoming_collections.length > 0 || summary.upcoming_payments.length > 0) && (
        <div data-testid="daily-summary" className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-amber-400"><AlertTriangle className="h-4 w-4" /> Kritik Stok</div>
            <div className="mt-1 font-display text-2xl font-semibold">{summary.critical_stock.count}</div>
            <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
              {summary.critical_stock.items.slice(0, 3).map((i, k) => (
                <div key={k} className="truncate">• {i.name}</div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-emerald-400"><ArrowDownCircle className="h-4 w-4" /> Yaklaşan Tahsilatlar (14g)</div>
            <div className="mt-1 font-display text-2xl font-semibold">{summary.upcoming_collections.length}</div>
            <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
              {summary.upcoming_collections.slice(0, 3).map((c, k) => (
                <div key={k} className={`truncate ${c.overdue ? "text-rose-400" : ""}`}>• {c.title} — {money(c.amount)} ({c.due_date})</div>
              ))}
              {summary.upcoming_collections.length === 0 && <div>Yaklaşan tahsilat yok</div>}
            </div>
          </div>
          <div className="rounded-xl border border-rose-500/25 bg-rose-500/5 p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-rose-400"><ArrowUpCircle className="h-4 w-4" /> Yaklaşan Ödemeler (14g)</div>
            <div className="mt-1 font-display text-2xl font-semibold">{summary.upcoming_payments.length}</div>
            <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
              {summary.upcoming_payments.slice(0, 3).map((p, k) => (
                <div key={k} className={`truncate ${p.overdue ? "text-rose-400" : ""}`}>• {p.title} — {money(p.amount)} ({p.due_date})</div>
              ))}
              {summary.upcoming_payments.length === 0 && <div>Yaklaşan ödeme yok</div>}
            </div>
          </div>
        </div>
      )}

      <div className="flex h-[calc(100vh-220px)] flex-col overflow-hidden rounded-2xl border border-border bg-card">
        <div className="flex-1 space-y-4 overflow-y-auto p-5" data-testid="assistant-messages">
          {messages.length === 0 && (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/15 text-primary">
                <Sparkles className="h-7 w-7" />
              </div>
              <h3 className="font-display text-xl font-medium">Finans verilerinize sorun</h3>
              <p className="mt-1 max-w-md text-sm text-muted-foreground">
                Müşteri bakiyeleri, stok miktarları, KDV ve nakit akışı hakkında Türkçe soru sorabilirsiniz.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    data-testid="assistant-suggestion"
                    onClick={() => send(s)}
                    className="rounded-full border border-border bg-secondary/40 px-3.5 py-1.5 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} data-testid={`msg-${m.role}`} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary text-primary"}`}>
                {m.role === "user" ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
              </div>
              <div className={`max-w-[75%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary/50 text-foreground"}`}>
                {m.text ? renderRich(m.text) : (streaming && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : "")}
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="flex items-center gap-2 border-t border-border p-4"
        >
          <Input
            data-testid="assistant-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Örn: MORELLO FOOD SRL ne kadar borcu var?"
            disabled={streaming}
            className="flex-1"
          />
          <Button data-testid="assistant-send" type="submit" disabled={streaming || !input.trim()} className="gap-1.5">
            {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </form>
      </div>
    </div>
  );
}
